/*
 * @Author: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @Date: 2025-01-XX XX:XX:XX
 * @LastEditors: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @LastEditTime: 2025-10-29 16:42:53
 * @FilePath: \mpc-monorepo\features\mpc-common\src\utils\sort.ts
 * @Description: 多字段排序工具函数
 */
import { get } from 'lodash'

/**
 * 排序配置项类型
 */
export type SortConfigItem = {
  key: string
  sortOrder: 'ascend' | 'descend'
}

/**
 * 根据多字段级别排序数据,针对table的排序功能
 * @param data - 待排序的数据数组
 * @param sortConfig - 排序配置数组，包含需要排序的字段和排序方向
 * @param sortKeyLevel - 排序字段优先级数组，按照数组顺序进行多字段联合排序
 * @returns 排序后的新数组（不修改原数组）
 * 
 * @example
 * ```ts
 * const data = [
 *   { name: 'B', age: 20 },
 *   { name: 'A', age: 20 },
 *   { name: 'A', age: 18 }
 * ]
 * const sortConfig = [
 *   { key: 'name', sortOrder: 'ascend' },
 *   { key: 'age', sortOrder: 'descend' }
 * ]
 * const sortKeyLevel = ['name', 'age']
 * const result = multiFieldSort(data, sortConfig, sortKeyLevel)
 * // 结果：[{ name: 'A', age: 20 }, { name: 'A', age: 18 }, { name: 'B', age: 20 }]
 * ```
 */
export function multiFieldSort<T extends Record<string, any>>(
  data: T[],
  sortConfig: SortConfigItem[],
  sortKeyLevel: string[]
): T[] {
  // 如果没有排序配置，直接返回原数组的副本
  if (!sortConfig || sortConfig.length === 0) {
    return [...data]
  }

  // 拷贝data数组，避免直接修改原始数据
  return [...data].sort((a, b) => {
    // 遍历优先级字段，依次判断是否有需要排序的字段项
    for (let i = 0; i < sortKeyLevel.length; i++) {
      const key = sortKeyLevel[i]
      const sortConfigItem = sortConfig.find(item => item.key === key)

      // 如果当前字段没有排序要求，跳至下一个字段
      if (!sortConfigItem) {
        continue
      }

      const sortOrder = sortConfigItem.sortOrder
      // 获取a、b两个对象的当前字段值
      const valueA = get(a, key)
      const valueB = get(b, key)

      // 进行对比：数字、字符串分开判断
      let compareResult = 0
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        compareResult = valueA - valueB
      } else if (typeof valueA === 'string' && typeof valueB === 'string') {
        compareResult = valueA.localeCompare(valueB, 'zh-Hans-CN', { numeric: true })
      } else if (valueA == null && valueB != null) {
        compareResult = -1
      } else if (valueA != null && valueB == null) {
        compareResult = 1
      } else {
        compareResult = 0
      }

      // 根据设定的升降序返回排序结果
      if (compareResult !== 0) {
        return sortOrder === 'ascend' ? compareResult : -compareResult
      }
      // 若本字段相等，继续下一个字段
    }
    // 如果所有字段都相等，返回0（顺序不变）
    return 0
  })
}

